import React, { useState, useEffect } from 'react';
import { Grid, Card, CardContent, Typography, Box } from '@mui/material';
import {
  AttachMoney,
  Business,
  LocationOn,
  ViewModule
} from '@mui/icons-material';

function StatisticsDashboard({ stats }) {
  const [animatedStats, setAnimatedStats] = useState({
    totalAmount: 0,
    providers: 0,
    locations: 0,
    units: 0
  });

  // Animate number transitions
  useEffect(() => {
    const duration = 500; // ms
    const steps = 30;
    const interval = duration / steps;

    const startValues = { ...animatedStats };
    const targetValues = {
      totalAmount: stats.totalAmount || 0,
      providers: stats.providers || 0,
      locations: stats.locations || 0,
      units: stats.units || 0
    };

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;

      setAnimatedStats({
        totalAmount: startValues.totalAmount + (targetValues.totalAmount - startValues.totalAmount) * progress,
        providers: startValues.providers + (targetValues.providers - startValues.providers) * progress,
        locations: startValues.locations + (targetValues.locations - startValues.locations) * progress,
        units: startValues.units + (targetValues.units - startValues.units) * progress
      });

      if (step >= steps) {
        clearInterval(timer);
        setAnimatedStats(targetValues);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [stats]);

  const statCards = [
    {
      label: 'Total Amount',
      value: animatedStats.totalAmount,
      format: 'currency',
      icon: AttachMoney,
      color: 'primary.main'
    },
    {
      label: 'Providers',
      value: animatedStats.providers,
      format: 'number',
      icon: Business,
      color: 'secondary.main'
    },
    {
      label: 'Locations',
      value: animatedStats.locations,
      format: 'number',
      icon: LocationOn,
      color: 'success.main'
    },
    {
      label: 'Units',
      value: animatedStats.units,
      format: 'number',
      icon: ViewModule,
      color: 'info.main'
    }
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {statCards.map((stat, index) => (
        <Grid item xs={6} sm={6} md={3} key={index}>
          <Card
            elevation={2}
            sx={{
              height: '100%',
              background: `linear-gradient(135deg, ${stat.color}15 0%, ${stat.color}05 100%)`,
              borderLeft: 3,
              borderColor: stat.color
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <stat.icon sx={{ color: stat.color, fontSize: 24, mr: 1 }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 500 }}
                >
                  {stat.label}
                </Typography>
              </Box>
              <Typography
                variant="h5"
                component="div"
                sx={{
                  fontWeight: 700,
                  color: stat.color,
                  fontFamily: '"Roboto Mono", monospace'
                }}
              >
                {formatValue(stat.value, stat.format)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

function formatValue(value, format) {
  if (format === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }
  
  if (format === 'number') {
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  }
  
  return value;
}

export default StatisticsDashboard;

